import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import ResumeForm from "./resume-form";
import {
  CandidateRequestModel,
  JobApplicationRequestModel,
} from "./career-page.models";
import { JobFormModel } from "../../shared/models";
import { EditCareerSiteModel } from "../../pages/career-page/job-detail/job-detail.model";

type JobApplicationProps = {
  jobTitle: string;
  color: string;
  radius: string;
  privacyPolicyLinks: string[];
  positionId: string;
  companyId: string;
  jobId: string;
  boardCode: string;
  questions?: JobFormModel[];
  careerSettings: EditCareerSiteModel[];
};

const JobApplication: React.FC<JobApplicationProps> = ({
  jobTitle,
  privacyPolicyLinks,
  companyId,
  positionId,
  jobId,
  radius,
  color,
  boardCode,
  questions,
  careerSettings,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (formData: any) => {
    setLoading(true);

    const creationDateTime = new Date().toISOString();

    const candidate: CandidateRequestModel = {
      name: formData.name.split(" ")[0],
      archiveID: formData.archiveID,
      storagePosition: formData.storagePosition,
      familyName: formData.familyName,
      contact: formData.email,
      cvFileName: formData.resumeFile ? formData.resumeFile.name : "",
      cvInBase64: formData.resume ? formData.resume.toString() : "",
      address: formData.address,
      fiscalCode: formData.fiscalCode,
      formAnswers: formData.formAnswers,
      // Added mandatory field values
      telephoneNumber: formData.phone,
      languages: formData.languages,
      applicantPreferences: formData.applicantPreferences,
      citizenWorkStatus: formData.citizenWorkStatus,
      commercialLicense: formData.commercialLicense,
      dateOfBirth: formData.birthDate,
      desiredSalary: formData.desiredSalary,
      disabilityPercentage: formData.disabilityPercentage,
      drivingLicense: formData.drivingLicense,
      felonyConviction: formData.felonyConviction,
      gender: formData.gender,
      jobQualification: formData.jobQualification,
      links: formData.links,
      pilotFlightHours: formData.pilotFlightHours,
      protectedCategoryStatus: formData.protectedCategoryStatus,
      softSkills: formData.softSkills,
      willingnessToTravel: formData.willingnessToTravel,
      workAvailability: formData.workAvailability,
      certifications: formData.certifications,
      hobbyAndInterests: formData.hobbyAndInterests,
      nationality: formData.nationality,
      convyData: {
        newsletterConsent: formData.convyData.newletterConsent,
        schoolTypeID: formData.convyData.schoolTypeID || "",
        studyPathID: formData.convyData.studyPathID || "",
        disciplineID: formData.convyData.disciplineID || "",
      },
      hardSkills: formData.hardSkills || [],
    };

    const apiUrl = process.env.REACT_APP_API_URL + "/postCandidateFromCareerPages";
    const apiKey = process.env.REACT_APP_GET_JOBPOSTS_SERVER_API_KEY;

    const jobApplicationRequest: JobApplicationRequestModel = {
      jobRef: companyId + "__" + positionId + "__" + jobId,
      jobTitle: jobTitle,
      creationDateTime: creationDateTime,
      boardCode: boardCode,
      candidate: candidate,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(jobApplicationRequest),
      });

      if (response.ok) {
        const thankYouMessage = intl.formatMessage({
          id: "careerPageApply.successMessage",
          defaultMessage: "Resume updated successfully!",
        });
        navigate("/thank-you-application-page", {
          state: { message: thankYouMessage },
        });
      } else {
        alert(
          intl.formatMessage({
            id: "careerPageApply.errorMessage",
            defaultMessage: "Failed to update resume. Please try again later.",
          })
        );
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      alert("Failed to submit application. Please try again later.");
    }
  };

  return (
    <ResumeForm
      onSubmit={handleSubmit}
      loading={loading}
      setLoading={setLoading}
      privacyPolicyLinks={privacyPolicyLinks}
      color={color}
      radius={radius}
      questions={questions}
      formTitle={intl.formatMessage({
        id: "careerPageApply.applyForJob",
        defaultMessage: "Apply for Job",
      })}
      buttonText={intl.formatMessage({
        id: "careerPageApply.submit",
        defaultMessage: "Submit",
      })}
      careerSettings={careerSettings}
      boardCode={boardCode}
      customerId={companyId}
    />
  );
};

export default JobApplication;
